import React, { useState, useEffect, useRef, createContext, useContext, useCallback } from 'react'; // Core React
import { useStudents } from 'shared/contexts/Student';
import { useTranslation } from 'shared/contexts/I18nContext';
import { useSubscription } from 'shared/contexts/Subscription';
import ContentHeaderSection from 'shared/common/ContentHeaderSection';
import FeatureLimitDisplay from 'shared/common/FeatureLimitDisplay';
import { useMasterList } from 'shared/hooks/useMasterList';
import { useLoading } from 'shared/contexts/Loading';
import { ProtectedVisibility, ADMIN_USERS } from 'shared/security/ProtectedRoute';
import { getCurrentUserProfile } from 'shared/utils/SessionDataUtils';
import apiCall from 'shared/services/ApiAdapter';
// Displays a list of all students and allows adding new students.
const StudentsContent = ({ navigateTo, useMockData, displayToast }) => {
    const { students, fetchStudents } = useStudents();
    const { t } = useTranslation();
    const { limits, canPerformAction } = useSubscription();

    // State to manage which student's menu is open
    const [openMenuId, setOpenMenuId] = useState(null);
    const menuRef = useRef(null);

    // The useLoading hook should be from your context
    const { startLoading, stopLoading } = useLoading();

    // Use the master list hook to fetch curricula and academic classes
    const {
        masterData: { curricula, academicClasses },
        isLoading: masterDataLoading,
        error: masterDataError
    } = useMasterList({ displayToast, startLoading, stopLoading });

    // State to manage tutors for beta users
    const [tutors, setTutors] = useState([]);
    const [selectedTutorUserId, setSelectedTutorUserId] = useState('');

    useEffect(() => {
        const fetchTutorsIfBeta = async () => {
            const profile = getCurrentUserProfile();
            if (ADMIN_USERS.includes(profile?.email?.toLowerCase())) {
                try {
                    const data = await apiCall('/tutors', useMockData, 'GET', null, startLoading, stopLoading, displayToast);
                    setTutors(data);
                } catch (e) {
                    console.error("Failed to fetch tutors", e);
                }
            }
        };
        fetchTutorsIfBeta();
    }, [useMockData, startLoading, stopLoading, displayToast]);

    // Effect to fetch students on component mount or when selected tutor changes
    useEffect(() => {
        fetchStudents(useMockData, displayToast, selectedTutorUserId);
    }, [useMockData, fetchStudents, displayToast, selectedTutorUserId]);

    // Effect to handle clicks outside of the kebab menu
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setOpenMenuId(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [menuRef]);

    const canAddStudent = canPerformAction('maxStudents', students.length);

    const toggleMenu = (studentId) => {
        setOpenMenuId(openMenuId === studentId ? null : studentId);
    };

    // Helper function to get the label from the master list data
    const getLabel = useCallback((value, list) => {
        const item = list.find(i => i.value === value);
        return item ? item.label : value;
    }, []);

    const handleEnrollStudent = (studentId, requestedCourseId) => {
        navigateTo("createBatchEnrollment", studentId, requestedCourseId);
    };

    const handleFindCourses = (studentId, requestedCourseId) => {
        navigateTo("findCourses", studentId);
    };


    const rightContentTop = (
        <>
            <div className="relative w-full max-w-40">
                <input
                    type="text"
                    placeholder={t('search')}
                    className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600 text-gray-900"
                />
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">🔍</span>
            </div>
            <ProtectedVisibility requiredRoles={[]} admin={true}>
                <div className="flex items-center gap-2 pl-2 pr-1 py-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus-within:ring-2 focus-within:ring-green-500 transition-colors">
                    <span className="px-2 py-0.5 text-[10px] font-bold tracking-wider text-purple-800 bg-purple-100 border border-purple-200 dark:bg-purple-900/40 dark:text-purple-300 dark:border-purple-800 rounded uppercase">
                        Admin
                    </span>
                    <select
                        value={selectedTutorUserId}
                        onChange={(e) => setSelectedTutorUserId(e.target.value)}
                        className="pr-4 py-1 bg-transparent font-medium text-sm text-gray-700 dark:text-gray-300 focus:outline-none cursor-pointer"
                    >
                        <option value="">My Students</option>
                        {tutors.map(tutor => {
                            const userId = tutor.user?.id || tutor.userId;
                            const userName = tutor.user?.firstName 
                                ? `${tutor.user.firstName} ${tutor.user.lastName || ''}`.trim() 
                                : tutor.user?.login || tutor.user?.email || tutor.name || userId;
                            return (
                                <option key={tutor.id} value={userId}>
                                    {userName}
                                </option>
                            );
                        })}
                    </select>
                </div>
            </ProtectedVisibility>
            <div className="flex items-center justify-center w-10 h-10 text-xl text-gray-100 bg-gray-800 rounded-full cursor-pointer dark:bg-gray-600 dark:text-gray-200">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-gray-100 dark:text-gray-200">
                    <path d="M10 18H14V16H10V18ZM3 6V8H21V6H3ZM6 13H18V11H6V13Z" fill="currentColor" />
                </svg>
            </div>
            <button
                onClick={() => navigateTo('createStudent')}
                className={`px-5 py-2 rounded-lg font-medium transition-colors ${canAddStudent ? 'bg-gray-800 text-gray-100 hover:bg-gray-700 dark:bg-gray-600 dark:hover:bg-gray-500' : 'bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-700 dark:text-gray-400'}`}
                disabled={!canAddStudent}
            >
                {t('addStudent')}
            </button>
        </>
    );

    const isLoading = students === null || masterDataLoading;

    if (masterDataError) {
        return (
            <div className="p-8 md:px-10 text-center text-red-500">
                {masterDataError}
            </div>
        );
    }

    return (
        <div className="p-8 md:px-10">
            <ContentHeaderSection
                title={t('students')}
                subtitle={t('viewManageStudentRoster')}
                rightContentTop={rightContentTop}
                rightContentBottom={<FeatureLimitDisplay feature="maxStudents" currentCount={students ? students.length : 0} limit={limits.maxStudents} />}
            />

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {isLoading ? (
                    <div className="col-span-full text-center text-gray-600 dark:text-gray-400">Loading...</div>
                ) : (
                    students.length === 0 ? (
                        <div className="col-span-full text-center text-gray-600 dark:text-gray-400">{t('noStudentsFound')}</div>
                    ) : (
                        students.map(student => (
                            <div
                                key={student.id}
                                onClick={() => navigateTo("studentHub", student.id)}
                                className="flex flex-col items-start p-6 
                                    bg-gradient-to-br from-white to-gray-50 dark:from-gray-900 dark:to-gray-800
                                    rounded-xl border border-gray-300 dark:border-gray-700
                                    shadow-sm hover:shadow-md 
                                    gap-4 relative cursor-pointer group
                                    transition-transform duration-300 ease-out hover:scale-[1.01]"
                            >

                                <div className="flex items-center w-full">
                                    {student.profilePictureUrl ? (
                                        <img
                                            src={student.profilePictureUrl}
                                            alt={student.name}
                                            className="w-10 h-10 rounded-full mr-3 object-cover"
                                        />
                                    ) : (
                                        <div className="w-10 h-10 rounded-full mr-3 bg-gray-800 flex items-center justify-center text-gray-100 font-bold">
                                            {student.name.charAt(0)}
                                        </div>
                                    )}
                                    <div className="flex-grow">
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 leading-tight">{student.name}</h3>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">{getLabel(student.academicClass, academicClasses)}</p>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">{getLabel(student.curriculum, curricula)}</p>
                                    </div>

                                    {/* Kebab menu */}
                                    <div className="relative z-10">
                                        <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); toggleMenu(student.id); }}
                                            className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 focus:outline-none"
                                            aria-label="Student menu"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                                            </svg>
                                        </button>
                                        {openMenuId === student.id && (
                                            <div ref={menuRef} className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white dark:bg-gray-700 ring-1 ring-black ring-opacity-5">
                                                <div className="py-1">
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            navigateTo('updateStudent', student.id);
                                                            setOpenMenuId(null);
                                                        }}
                                                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600"
                                                    >
                                                        Edit
                                                    </button>
                                                    <ProtectedVisibility requiredRoles={['ROLE_TEACHER']}>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleEnrollStudent(student.id, student.requestedEnrollmentCourseId);
                                                            }}
                                                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600"
                                                            aria-label={`Enroll ${student.name}`}
                                                        >
                                                            {t("enroll")}
                                                        </button>
                                                    </ProtectedVisibility>
                                                    <ProtectedVisibility requiredRoles={['ROLE_TEACHER', 'ROLE_PARENT']}>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleFindCourses(student.id);
                                                            }}
                                                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600"
                                                            aria-label={`Enroll ${student.name}`}
                                                        >
                                                            {t("Find Courses")}
                                                        </button>
                                                    </ProtectedVisibility>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))
                    )
                )}
            </div>
        </div>
    );
};
export default StudentsContent;
